import { pdfSafe } from './pdfFont';
import { jsPDF } from 'jspdf';
import { MANUAL } from './manual';
import { buildManual, buildAquaManual, buildStormManual, buildPmManual } from './manualRef';
import { APP_INFO, getLogo } from './license';
import TREE from './featureTree.json';
import { drawCyclePdf } from './aquaCycle';

const plain = (s) => pdfSafe(s);
const loadImg = async (src) => {
  try {
    const b = await (await fetch(src)).blob();
    return await new Promise((r) => { const f = new FileReader(); f.onload = () => r(f.result); f.readAsDataURL(b); });
  } catch { return null; }
};

// book: 'main' = full manual, 'aqua' = separate AquaFlow office manual (DE/EN)
const book = (lang, kind) => {
  const m = MANUAL[lang] || MANUAL.de;
  if (kind === 'pm') { const { a, lang: l, chapters } = buildPmManual(lang); return { m: { ...(MANUAL[l] || MANUAL.en), title: a.title, intro: a.intro }, lang: l, chapters, storm: true, big: a.title.split(' – ')[0], file: l === 'de' ? 'GISS-Projektverwaltung-Handbuch' : 'GISS-Project-Management-Manual' }; }
  if (kind === 'storm') { const { a, lang: l, chapters } = buildStormManual(lang); return { m: { ...(MANUAL[l] || MANUAL.en), title: a.title, intro: a.intro }, lang: l, chapters, storm: true, file: l === 'de' ? 'GISS-Stormwater-Handbuch' : 'GISS-Stormwater-Manual' }; }
  if (kind !== 'aqua') return { m, lang, chapters: buildManual(lang).chapters, file: 'GISS-FieldRapport-Pro-Handbuch' };
  const { a, lang: l, chapters } = buildAquaManual(lang), ml = MANUAL[l];
  return { m: { ...ml, title: a.title, intro: a.intro }, lang: l, chapters, aqua: true, cyc: a.ui, file: l === 'de' ? 'GISS-AquaFlow-Handbuch' : 'GISS-AquaFlow-Manual' };
};

export async function manualPdf(lang0, kind) {
  const { m, lang, chapters, aqua, storm, big, file, cyc: cyc0 } = book(lang0, kind), cyc = cyc0 && { ...cyc0, cycleC: cyc0.cycleC.replace('⇄', '<->') };
  const d = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, M = 18, CW = W - 2 * M;
  const logo = getLogo();
  const head = () => {
    d.setFillColor(10, 37, 64); d.rect(0, 0, W, 16, 'F');
    d.setTextColor(255); d.setFont('helvetica', 'bold'); d.setFontSize(10); d.text(plain(m.title), M, 10);
    d.setFont('helvetica', 'normal'); d.setFontSize(8.5); d.text(`v${APP_INFO.version}`, W - M, 10, { align: 'right' });
    d.setTextColor(20); d.setFont('helvetica', 'normal');
  };
  // cover
  d.setFillColor(10, 37, 64); d.rect(0, 0, W, 120, 'F');
  if (logo) { try { d.addImage(logo, M, 20, 40, 20, undefined, 'FAST'); } catch { /* ignore */ } }
  d.setTextColor(255); d.setFont('helvetica', 'bold'); d.setFontSize(26); d.text(plain(big || (aqua ? 'AquaFlow' : storm ? 'Stormwater' : APP_INFO.name)), M, 70);
  d.setFontSize(15); d.setFont('helvetica', 'normal'); d.text(plain(m.title.split(' - ').pop().split(' – ').pop()), M, 82);
  d.setFillColor(255, 106, 19); d.rect(M, 92, 40, 2, 'F');
  d.setTextColor(30); d.setFontSize(11); const il = d.splitTextToSize(plain(m.intro), CW); d.text(il, M, 140);
  if (aqua) drawCyclePdf(d, W / 2, Math.max(140 + il.length * 5 + 52, 222), 34, cyc, plain);
  d.setFontSize(9); d.setTextColor(110); d.text(`${APP_INFO.publisher} (${APP_INFO.web}) · v${APP_INFO.version}`, M, 285);
  d.addPage(); // TOC placeholder
  const pages = [];
  for (const ch of chapters) {
    d.addPage(); head(); pages.push(d.getNumberOfPages());
    let y = 30;
    const room = (h) => { if (y + h > 280) { d.addPage(); head(); y = 30; } };
    d.setFont('helvetica', 'bold'); d.setFontSize(15); d.setTextColor(10, 37, 64);
    const tl = d.splitTextToSize(plain(`${ch.n}. ${ch.title}`), CW); d.text(tl, M, y); y += tl.length * 6.5 - 2;
    d.setFillColor(255, 106, 19); d.rect(M, y, 24, 1.2, 'F'); y += 9;
    const blocks = ch.subs ? ch.subs.flatMap((sb) => [{ head: sb.title }, ...sb.secs]) : ch.secs;
    for (const sec of blocks) {
      if (sec.head) { room(14); d.setFont('helvetica', 'bold'); d.setFontSize(12.5); d.setTextColor(194, 65, 12); const hl = d.splitTextToSize(plain(sec.head), CW); d.text(hl, M, y); y += hl.length * 5.6 + 3; continue; }
      room(16);
      d.setFont('helvetica', 'bold'); d.setFontSize(11.5); d.setTextColor(10, 37, 64);
      const st = d.splitTextToSize(plain(`${sec.id}  ${sec.title}`), CW); d.text(st, M, y); y += st.length * 5.2 + 2;
      d.setFont('helvetica', 'normal'); d.setFontSize(10); d.setTextColor(25);
      sec.steps.forEach((s, k) => {
        const lines = d.splitTextToSize(plain(s), CW - 9);
        room(lines.length * 4.7 + 2);
        d.setFillColor(29, 111, 232); d.circle(M + 3, y - 1.3, 2.7, 'F');
        d.setTextColor(255); d.setFontSize(7.5); d.text(String(k + 1), M + 3, y - 0.3, { align: 'center' });
        d.setTextColor(25); d.setFontSize(10); d.text(lines, M + 9, y); y += lines.length * 4.7 + 2.6;
      });
      if (sec.tip) {
        const tip = d.splitTextToSize(plain(`${m.tip}: ${sec.tip}`), CW - 8);
        room(tip.length * 4.7 + 6);
        d.setFillColor(255, 244, 214); d.roundedRect(M, y - 1, CW, tip.length * 4.7 + 5, 2, 2, 'F');
        d.setTextColor(90, 60, 0); d.text(tip, M + 4, y + 4); y += tip.length * 4.7 + 8;
      }
      if (sec.aq) {
        const rl = d.splitTextToSize(plain(`> ${sec.aq.title}`), CW - 4);
        room(rl.length * 4.5 + 3); d.setFont('helvetica', 'italic'); d.setFontSize(9.5); d.setTextColor(14, 116, 144); d.text(rl, M + 2, y); y += rl.length * 4.5 + 2;
        d.setFont('helvetica', 'normal'); d.setFontSize(10);
      }
      const shots = sec.imgs ? (await Promise.all(sec.imgs.map((im) => loadImg(`/manual/${lang}/${im}.jpg`)))).filter((u) => u && u.startsWith('data:image/jpeg')) : [];
      for (let r = 0; r < shots.length; r += 3) {
        room(84);
        shots.slice(r, r + 3).forEach((u, k) => { const x = M + k * 52; d.addImage(u, 'JPEG', x, y, 45, 80); d.setDrawColor(200); d.rect(x, y, 45, 80); });
        y += 86;
      }
      const img = sec.img ? await loadImg(`/manual/${lang}/${sec.img}.jpg`) : null;
      if (img) { room(84); d.addImage(img, 'JPEG', M, y, 45, 80); d.setDrawColor(200); d.rect(M, y, 45, 80); y += 86; }
      y += 3;
    }
  }
  // appendix: feature tree (crown = sectors, trunk = core, roots = foundation)
  const tr = TREE[lang] || TREE.de;
  if (!aqua && !storm) {
  d.addPage(); head(); pages.push(d.getNumberOfPages());
  let y = 30;
  d.setFont('helvetica', 'bold'); d.setFontSize(15); d.setTextColor(10, 37, 64); d.text(plain(tr.ui.title), M, y); y += 4;
  d.setFillColor(255, 106, 19); d.rect(M, y, 24, 1.2, 'F'); y += 9;
  for (const [lbl, gs] of [[tr.ui.crown, tr.crown], [tr.ui.trunk, tr.trunk], [tr.ui.roots, tr.roots]]) {
    if (y > 262) { d.addPage(); head(); y = 30; }
    d.setFont('helvetica', 'bold'); d.setFontSize(12.5); d.setTextColor(194, 65, 12); d.text(plain(lbl), M, y); y += 7;
    for (const g of gs) {
      const body = d.splitTextToSize(plain(g.c.map((x) => x.t).join(' · ')), CW - 4);
      if (y + 6 + body.length * 4.3 > 282) { d.addPage(); head(); y = 30; }
      d.setFont('helvetica', 'bold'); d.setFontSize(10.5); d.setTextColor(10, 37, 64); d.text(plain(`${g.t} – ${g.d}`), M, y); y += 5;
      d.setFont('helvetica', 'normal'); d.setFontSize(9); d.setTextColor(40); d.text(body, M + 4, y); y += body.length * 4.3 + 3;
    }
  }
  }
  const toc = aqua || storm ? chapters : [...chapters, { n: '', title: tr.ui.title }];
  d.setPage(2); head();
  d.setFont('helvetica', 'bold'); d.setFontSize(18); d.setTextColor(10, 37, 64); d.text(plain(m.toc), M, 34);
  d.setFont('helvetica', 'normal'); d.setFontSize(10.5); d.setTextColor(25);
  toc.forEach((ch, i) => {
    const y = 44 + i * 7.2;
    const tl = d.splitTextToSize(plain(ch.n === '' ? ch.title : `${ch.n}. ${ch.title}`), CW - 14);
    d.text(tl[0] + (tl.length > 1 ? ' ...' : ''), M, y);
    d.text(String(pages[i]), W - M, y, { align: 'right' });
    d.setDrawColor(225); d.line(M, y + 2.2, W - M, y + 2.2);
  });
  const n = d.getNumberOfPages();
  for (let p = 2; p <= n; p++) {
    d.setPage(p); d.setFontSize(8); d.setTextColor(120);
    d.text(plain(`${APP_INFO.name} v${APP_INFO.version} | ${APP_INFO.publisher} (${APP_INFO.web})`), M, 290);
    d.text(`${m.page} ${p} / ${n}`, W - M, 290, { align: 'right' });
  }
  d.save(`${file}-${lang.toUpperCase()}.pdf`);
}

// manual as Markdown (same chapters/sections as the PDF)
export function manualMd(lang0, kind) {
  const { m, lang, chapters, file, cyc } = book(lang0, kind);
  const out = [`# ${APP_INFO.name} – ${m.title}`, '', `Version ${APP_INFO.version} · ${APP_INFO.publisher} (${APP_INFO.web}) · ${new Date().toISOString().slice(0, 10)}`, '', m.intro || '', ''];
  if (cyc) out.push(`**${cyc.cycleC}:** ${cyc.cycle.map((x, i) => `${i + 1}. ${x}`).join(' → ')} → 1.`, '');
  const sec = (s) => { out.push(`### ${s.id} ${s.title}`, ''); (s.steps || []).forEach((x, i) => out.push(`${i + 1}. ${x}`)); if (s.tip) out.push('', `> ${s.tip}`); if (s.aq) out.push('', `→ *${s.aq.title}*`); out.push(''); };
  chapters.forEach((c) => {
    out.push(`## ${c.n}. ${c.title}`, '');
    if (c.subs) c.subs.forEach((sb) => { out.push(`#### ${sb.title}`, ''); sb.secs.forEach(sec); }); else c.secs.forEach(sec);
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([out.join('\n')], { type: 'text/markdown;charset=utf-8' }));
  a.download = `${file}-${lang}-v${APP_INFO.version}.md`;
  a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
